<script setup>
import { computed, onMounted, ref } from 'vue';
import { useLoadWithRetry } from 'dashboard/composables/loadWithRetry';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { downloadFile } from '@chatwoot/utils';
import Icon from 'next/icon/Icon.vue';
import Button from 'next/button/Button.vue';
import { useMessageContext } from '../provider.js';
import { MESSAGE_TYPES } from '../constants.js';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';
import AddToStickerButton from './AddToStickerButton.vue';

const { t } = useI18n();
const { attachments, messageType, filteredCurrentChatAttachments } =
  useMessageContext();
const attachment = computed(() => attachments.value[0]);
const { isLoaded, hasError, loadWithRetry } = useLoadWithRetry();
const showGallery = ref(false);
const isDownloading = ref(false);
const isIncoming = computed(() => messageType.value === MESSAGE_TYPES.INCOMING);

onMounted(() => {
  if (attachment.value?.dataUrl) {
    loadWithRetry(attachment.value.dataUrl);
  }
});

const downloadAttachment = async () => {
  const { fileType, dataUrl, extension } = attachment.value;
  try {
    isDownloading.value = true;
    await downloadFile({ url: dataUrl, type: fileType, extension });
  } catch (error) {
    useAlert(t('GALLERY_VIEW.ERROR_DOWNLOADING'));
  } finally {
    isDownloading.value = false;
  }
};
</script>

<template>
  <div
    data-bubble-name="sticker"
    class="group relative flex min-h-24 min-w-24 cursor-pointer items-center justify-center p-1"
    @click="showGallery = true"
  >
    <div v-if="hasError" class="flex items-center gap-1 text-n-slate-11">
      <Icon icon="i-lucide-circle-off" />
      <span class="text-sm">
        {{ $t('COMPONENTS.MEDIA.IMAGE_UNAVAILABLE') }}
      </span>
    </div>
    <img
      v-else-if="isLoaded"
      :src="attachment.dataUrl"
      :alt="attachment.extension || 'sticker'"
      class="skip-context-menu block max-h-56 max-w-56 object-contain"
    />
    <div
      v-if="isLoaded"
      class="absolute bottom-2 right-2 hidden gap-2 group-hover:flex"
    >
      <AddToStickerButton v-if="isIncoming" :attachment-id="attachment.id" />
      <Button xs solid slate icon="i-lucide-expand" class="opacity-60" />
      <Button
        xs
        solid
        slate
        icon="i-lucide-download"
        class="opacity-60"
        :is-loading="isDownloading"
        :disabled="isDownloading"
        @click.stop="downloadAttachment"
      />
    </div>
  </div>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="hasError = true"
    @close="showGallery = false"
  />
</template>
